import { useState, useEffect } from 'react';
import { X, Camera, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toPng } from 'html-to-image';
import { useReportStore } from '../../stores/reportStore';
import { feedbackApi } from '../../services/api';
import { FEEDBACK_CATEGORIES, FEEDBACK_SUBCATEGORIES, type FeedbackCategory, type FeedbackSubcategory } from '../../types';

interface Props {
  onClose: () => void;
}

/**
 * Modal that bundles a screenshot + the current session state + the user's
 * description and ships it to /api/feedback. Designed to be one click for the
 * user: pick a category, write a sentence, hit send.
 */
export default function FeedbackDialog({ onClose }: Props) {
  const [category, setCategory] = useState<FeedbackCategory>('critical');
  const [subcategory, setSubcategory] = useState<FeedbackSubcategory | null>(null);
  const [content, setContent] = useState('');
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [screenshotStatus, setScreenshotStatus] = useState<'idle' | 'capturing' | 'ready' | 'failed'>('idle');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const [screenshotError, setScreenshotError] = useState<string | null>(null);

  // Capture screenshot once when the dialog opens (before it itself paints).
  // We hide the dialog while snapping so the screenshot reflects the page the
  // user was actually looking at — otherwise we'd capture our own overlay.
  useEffect(() => {
    if (!includeScreenshot || screenshotStatus !== 'idle') return;
    let cancelled = false;
    const dialogEl = document.getElementById('feedback-dialog-root');
    if (dialogEl) dialogEl.style.visibility = 'hidden';
    setScreenshotStatus('capturing');
    setScreenshotError(null);

    // 250ms before capture so the dialog hide + any layout shift has settled.
    // Anything <150ms intermittently grabs the dialog itself on slower machines.
    const timer = setTimeout(async () => {
      // Hard timeout — if html-to-image hangs (rare but seen on pages with
      // pending font loads), give up after 8s rather than blocking the user.
      const captureTimeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Screenshot capture timed out after 8s')), 8000)
      );

      try {
        const dataUrl = await Promise.race([
          toPng(document.body, {
            cacheBust: true,
            pixelRatio: 1,
            // Skip embedding webfonts. We lose pixel-perfect typography but
            // avoid CORS-tainted-canvas failures from font CDNs entirely.
            skipFonts: true,
            filter: (node) => {
              if (node instanceof HTMLElement) {
                if (node.id === 'feedback-dialog-root') return false;
                if (node.dataset?.noScreenshot !== undefined) return false;
              }
              return true;
            },
          }),
          captureTimeout,
        ]);
        if (!cancelled) {
          setScreenshot(dataUrl);
          setScreenshotStatus('ready');
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error('[feedback] screenshot capture failed:', err);
        if (!cancelled) {
          setScreenshotStatus('failed');
          setScreenshotError(msg);
        }
      } finally {
        if (dialogEl) dialogEl.style.visibility = 'visible';
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (dialogEl) dialogEl.style.visibility = 'visible';
    };
  }, [includeScreenshot, screenshotStatus]);

  // When the user toggles screenshot off after capture, drop the data.
  useEffect(() => {
    if (!includeScreenshot) {
      setScreenshot(null);
      setScreenshotStatus('idle');
    }
  }, [includeScreenshot]);

  const handleSubmit = async () => {
    if (!content.trim()) {
      setError('Please describe the issue or feedback.');
      return;
    }
    if (category === 'model_quality' && !subcategory) {
      setError('Pick which kind of report-quality issue this is — it helps us bucket the data.');
      return;
    }
    setSubmitting(true);
    setError(null);

    // Grab the current store state at submit time — not at open time —
    // so the bundle reflects whatever the user did between opening the dialog
    // and clicking submit (rare but possible).
    const s = useReportStore.getState();

    try {
      await feedbackApi.submit({
        category,
        subcategory: category === 'model_quality' ? subcategory ?? undefined : undefined,
        content: content.trim(),
        transcription: s.transcription || undefined,
        raw_report: s.rawReport || undefined,
        final_report: s.editedReport || undefined,
        model_used: s.modelUsed || undefined,
        primary_model: s.primaryModel || undefined,
        exam_category: s.examCategory || undefined,
        template_id: s.templateId || undefined,
        template_name: s.templateName || undefined,
        decision_id: s.decisionId || undefined,
        url: window.location.href,
        user_agent: navigator.userAgent,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        screenshot: includeScreenshot && screenshot ? screenshot : undefined,
      });
      setSubmitted(true);
      // Auto-close after 1.8s so the user sees the success state
      setTimeout(onClose, 1800);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send feedback';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        id="feedback-dialog-root"
        data-no-screenshot
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      >
        <div className="bg-white dark:bg-surface-900 rounded-lg shadow-xl p-6 max-w-sm text-center">
          <CheckCircle2 size={40} className="mx-auto text-green-600 mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Thanks — feedback sent.</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">We'll review it shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      id="feedback-dialog-root"
      data-no-screenshot
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="bg-white dark:bg-surface-900 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-surface-800">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Send feedback</h2>
          <button
            onClick={onClose}
            disabled={submitting}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto flex-1">
          {/* Category */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              What kind of feedback?
            </label>
            <div className="space-y-1.5">
              {FEEDBACK_CATEGORIES.map((cat) => (
                <label
                  key={cat.value}
                  className={`flex items-start gap-2 px-3 py-2 rounded border cursor-pointer transition-colors ${
                    category === cat.value
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-surface-800 hover:bg-gray-50 dark:hover:bg-surface-850'
                  }`}
                >
                  <input
                    type="radio"
                    name="feedback-category"
                    value={cat.value}
                    checked={category === cat.value}
                    onChange={() => setCategory(cat.value)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{cat.label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{cat.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Subcategory — only shown for model_quality */}
          {category === 'model_quality' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                What kind of report-quality issue?
              </label>
              <div className="space-y-1.5">
                {FEEDBACK_SUBCATEGORIES.map((sub) => (
                  <label
                    key={sub.value}
                    className={`flex items-start gap-2 px-3 py-2 rounded border cursor-pointer transition-colors ${
                      subcategory === sub.value
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-surface-800 hover:bg-gray-50 dark:hover:bg-surface-850'
                    }`}
                  >
                    <input
                      type="radio"
                      name="feedback-subcategory"
                      value={sub.value}
                      checked={subcategory === sub.value}
                      onChange={() => setSubcategory(sub.value)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{sub.label}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{sub.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div className="mb-4">
            <label htmlFor="feedback-content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Describe what happened
            </label>
            <textarea
              id="feedback-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="e.g., Clicked Generate and got 'Report generation failed'. Was working on a chest CT."
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 dark:border-surface-800 rounded-md bg-white dark:bg-surface-850 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={submitting}
            />
          </div>

          {/* Screenshot toggle */}
          <div className="mb-2 flex items-start gap-2">
            <input
              type="checkbox"
              id="feedback-screenshot"
              checked={includeScreenshot}
              onChange={(e) => setIncludeScreenshot(e.target.checked)}
              className="mt-0.5"
              disabled={submitting}
            />
            <label htmlFor="feedback-screenshot" className="text-sm text-gray-700 dark:text-gray-300 flex-1">
              <span className="flex items-center gap-1">
                <Camera size={14} />
                Include screenshot
              </span>
              <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Captures the current page so we can see exactly what you saw. Your current transcription and report are also attached automatically.
              </span>
            </label>
          </div>

          {/* Screenshot status */}
          {includeScreenshot && (
            <div className="ml-6 mt-1">
              {screenshotStatus === 'capturing' && (
                <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <Loader2 size={12} className="animate-spin" /> Capturing screenshot…
                </span>
              )}
              {screenshotStatus === 'ready' && (
                <span className="inline-flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle2 size={12} /> Screenshot ready
                </span>
              )}
              {screenshotStatus === 'failed' && (
                <div className="p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded text-xs">
                  <div className="flex items-center gap-1 text-amber-800 dark:text-amber-300 font-medium">
                    <AlertCircle size={12} /> Screenshot capture failed
                  </div>
                  <div className="text-amber-700 dark:text-amber-300/80 mt-1">
                    Feedback will be sent without a screenshot.{' '}
                    {screenshotError && <span className="block mt-1 font-mono text-[10px] opacity-75">{screenshotError}</span>}
                  </div>
                  <button
                    onClick={() => setScreenshotStatus('idle')}
                    className="mt-1.5 text-amber-700 dark:text-amber-300 underline text-xs"
                  >
                    Try again
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded text-xs text-red-700 dark:text-red-300">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-surface-800">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-850 rounded disabled:opacity-50"
          >
            Cancel
          </button>
          {(() => {
            const waitingForScreenshot = includeScreenshot && screenshotStatus === 'capturing';
            const disabled = submitting || !content.trim() || waitingForScreenshot;
            return (
              <button
                onClick={handleSubmit}
                disabled={disabled}
                title={waitingForScreenshot ? 'Waiting for screenshot capture to finish…' : undefined}
                className="px-3 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              >
                {(submitting || waitingForScreenshot) ? <Loader2 size={14} className="animate-spin" /> : null}
                {waitingForScreenshot ? 'Capturing…' : 'Send feedback'}
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
